import { useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import type { PublicNavItem } from '@/constants/user-menu'
import { isNavItemActive, resolveNavItemHref } from '@/constants/user-menu'
import { useAuth, useSiteBrand } from '@/hooks'
import { ContestNavLink } from '@/features/contests/components/ContestNavLink'
import { StudioNavLink } from '@/features/studio/components/StudioNavLink'
import { LangSwitcher } from './LangSwitcher'
import { SubscriptionNavLink } from './SubscriptionNavLink'
import { Button } from '@/shared/components/ui/button'

type SiteMobileNavProps = {
  open: boolean
  onClose: () => void
  publicItems: PublicNavItem[]
  authedItems: PublicNavItem[]
  showStudioNav?: boolean
  isAuthenticated: boolean
  showSubscriptionNav?: boolean
}

function MobileNavItem({
  item,
  onNavigate,
}: {
  item: PublicNavItem
  onNavigate: () => void
}) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const active = isNavItemActive(pathname, item)
  const href = resolveNavItemHref(item)

  return (
    <NavLink
      to={href}
      end={item.end}
      onClick={onNavigate}
      className={clsx('portal-drawer-link', active && 'is-active')}
    >
      <span className="portal-nav-link__text">{t(item.i18nKey)}</span>
    </NavLink>
  )
}

export function SiteMobileNav({
  open,
  onClose,
  publicItems,
  authedItems,
  showStudioNav = false,
  isAuthenticated,
  showSubscriptionNav = false,
}: SiteMobileNavProps) {
  const { t } = useTranslation()
  const { brandName, siteName, hasLogo } = useSiteBrand()
  const drawerTitle = brandName || (!hasLogo ? siteName : '')
  const { logout } = useAuth()
  const location = useLocation()
  const pathnameRef = useRef(location.pathname)

  useEffect(() => {
    if (pathnameRef.current === location.pathname) return
    pathnameRef.current = location.pathname
    onClose()
  }, [location.pathname, onClose])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        className={clsx('portal-drawer-backdrop h-auto w-auto rounded-none p-0', open && 'is-open')}
        aria-label={t('common.close')}
        onClick={onClose}
      />

      <aside
        className={clsx('portal-drawer', open && 'is-open')}
        role="dialog"
        aria-modal="true"
        aria-label={t('common.siteNav')}
        aria-hidden={!open}
      >
        <div className="portal-drawer-head">
          {drawerTitle ? <strong className="portal-drawer-title">{drawerTitle}</strong> : <span />}
          <Button type="button" variant="ghost" className="portal-drawer-close h-auto" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>

        <nav className="portal-drawer-nav">
          {publicItems.map((item) =>
            item.to === '/contests' ? (
              <ContestNavLink key={item.to} variant="drawer" onNavigate={onClose} />
            ) : (
              <MobileNavItem key={item.to} item={item} onNavigate={onClose} />
            ),
          )}
          {(authedItems.length > 0 || showStudioNav) && (
            <>
              <div className="portal-drawer-divider" role="separator" />
              {showStudioNav ? <StudioNavLink variant="drawer" onNavigate={onClose} /> : null}
              {authedItems.map((item) => (
                <MobileNavItem key={item.to} item={item} onNavigate={onClose} />
              ))}
            </>
          )}
        </nav>

        <div className="portal-drawer-foot">
          {showSubscriptionNav && <SubscriptionNavLink variant="drawer" onNavigate={onClose} />}

          <LangSwitcher variant="drawer" fullWidth className="portal-drawer-lang" />

          {isAuthenticated ? (
            <>
              <Link to="/account/dashboard" className="portal-drawer-foot-link" onClick={onClose}>
                {t('userMenu.title')}
              </Link>
              <Button
                type="button"
                variant="ghost"
                className="portal-drawer-logout h-auto w-full justify-start"
                onClick={() => {
                  onClose()
                  void logout()
                }}
              >
                {t('auth.logout')}
              </Button>
            </>
          ) : (
            <Button asChild variant="portal" className="inline-flex w-full portal-drawer-login">
              <Link to="/auth/login" onClick={onClose}>
                {t('auth.enter')}
              </Link>
            </Button>
          )}
        </div>
      </aside>
    </>
  )
}
